import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, Tooltip } from "@mui/material";
import AddPatient from "@/components/app/admin/provider-groups/AddPatient";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { FilterSelect } from "@/components/FilterSelect";
import Flex from "@/components/Flex";
import LabeledSwitch from "@/components/LabeledSwitch";
import SearchInput from "@/components/SearchInput";
import { useRoleController } from "@/components/useRoleController";
import { STATE_LIST } from "@/constants";
import useDisclosure from "@/hooks/use-disclosure";
import { getBaseRole } from "@/utils/auth-storage";
import AuditLogFilter from "../audit-log/AuditLogFilter";
import {
  AuditLogFilters,
  SEARCH_PLACEHOLDER_BY_TAB,
} from "../practice-setting-constant";

export interface PracticeSettingActionsProps {
  activeButton: string;
  searchKey: string;
  onSearch: (value: string) => void;
  stateFilter?: string[];
  onStateFilterChange: (value?: string[]) => void;
  showArchivedAppointmentType: boolean;
  onShowArchivedAppointmentTypeChange: (value: boolean) => void;
  showArchivedReferringAgency: boolean;
  onShowArchivedReferringAgencyChange: (value: boolean) => void;
  auditLogFilters: AuditLogFilters;
  setAuditLogFilters: (filters: AuditLogFilters) => void;
  onEditProfile: () => void;
  onAddLocation: () => void;
  onAddDepartment: () => void;
  onAddReferringAgency: () => void;
  onAddAppointmentType: () => void;
}

const PracticeSettingActions = ({
  activeButton,
  searchKey,
  onSearch,
  stateFilter,
  onStateFilterChange,
  showArchivedAppointmentType,
  onShowArchivedAppointmentTypeChange,
  showArchivedReferringAgency,
  onShowArchivedReferringAgencyChange,
  auditLogFilters,
  setAuditLogFilters,
  onEditProfile,
  onAddLocation,
  onAddDepartment,
  onAddReferringAgency,
  onAddAppointmentType,
}: PracticeSettingActionsProps) => {
  const addPatientModalDisclosure = useDisclosure(false);
  const isPracticeAdmin = getBaseRole() === "PRACTICE_ADMIN";
  const { hasUpdate: hasAuditLogUpdate, hasView: hasAuditLogView } =
    useRoleController("Settings Audit Log");
  const searchPlaceholder = SEARCH_PLACEHOLDER_BY_TAB[activeButton];

  return (
    <Flex gap={2} justifyContent={"end"} alignItems={"center"}>
      {activeButton === "referring-agency" && (
        <LabeledSwitch
          label="Show Archived"
          checked={showArchivedReferringAgency}
          onChange={(e) =>
            onShowArchivedReferringAgencyChange(e.target.checked)
          }
        />
      )}

      {searchPlaceholder && (
        <SearchInput
          searchKey={searchKey}
          onSearch={onSearch}
          placeholder={searchPlaceholder}
          sx={{ width: "391px" }}
        />
      )}

      {activeButton === "locations" && (
        <Box minWidth={"15rem"}>
          <FilterSelect
            placeholder="States"
            value={stateFilter?.[0] ?? null}
            options={STATE_LIST}
            onChange={(val) => onStateFilterChange(val ? [val] : undefined)}
          />
        </Box>
      )}

      {activeButton === "profile" && (
        <StyledButtonV2
          variant="outlined"
          label="Edit Profile"
          onClick={onEditProfile}
          startIcon={<ModeEditOutlineOutlinedIcon />}
        />
      )}

      {activeButton === "locations" && (
        <StyledButtonV2
          label="Add Location"
          onClick={onAddLocation}
          shouldAddIcon
        />
      )}

      {activeButton === "departments" && (
        <StyledButtonV2
          label="Add Department"
          onClick={onAddDepartment}
          shouldAddIcon
        />
      )}

      {activeButton === "referring-agency" && (
        <StyledButtonV2
          label="Add Referring Agency"
          onClick={onAddReferringAgency}
          shouldAddIcon
        />
      )}

      {activeButton === "patients" && (
        <StyledButtonV2
          label="Add Patient"
          onClick={addPatientModalDisclosure.open}
          shouldAddIcon
        />
      )}
      {activeButton === "appointment-type-configuration" && (
        <>
          <Tooltip
            title="Appointment Types define visit duration and color coding for provider schedules."
            placement="bottom"
            arrow
            style={{}}
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
          <LabeledSwitch
            label="Show Archived Appointment Types"
            checked={showArchivedAppointmentType}
            onChange={(e) =>
              onShowArchivedAppointmentTypeChange(e.target.checked)
            }
          />
          <Box width={"26%"}>
            <SearchInput
              searchKey={searchKey}
              onSearch={onSearch}
              placeholder="Search By Appointment Type"
            />
          </Box>
          <StyledButtonV2
            label="Add Appointment Type"
            onClick={onAddAppointmentType}
            disabled={!isPracticeAdmin}
            shouldAddIcon
          />
        </>
      )}
      {activeButton === "audit-log" &&
        (hasAuditLogUpdate || hasAuditLogView) && (
          <AuditLogFilter
            filters={auditLogFilters}
            setAuditLogFilters={setAuditLogFilters}
          />
        )}

      {addPatientModalDisclosure.opened && (
        <AddPatient
          modalTitle="Add Patient"
          open={addPatientModalDisclosure.opened}
          onClose={addPatientModalDisclosure.close}
        />
      )}
    </Flex>
  );
};

export default PracticeSettingActions;
